import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Container, Typography, Grid, Box, Chip, Button } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import PropTypes from 'prop-types';
import { useAtomValue } from 'jotai';
import DateRangePicker from './DateRangePicker';
import { userBranchesAtom, userDataAtom } from '../../Atoms';
import useAPI from '../../api';
import {
  createNewDate,
  calculateDaysBetweenRange,
  culture,
  options,
} from '../../Helpers/dates';
import { formatWavePeriodValue } from '../../Utils/constants/general';
import capitalize from '../../Helpers/StringsFormatting/capitalize';
import ReportSelect from '../../Components/Inputs/ReportSelect';
import { CenteredFlexBox } from '../../Components/FlexBox';
import ThemeFab from '../../Components/Button/ThemeFab';
import Fab from '../../Components/Navbar/Fab';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import WavePeriodDialog from '../PivotReport/Outbound/Components/WavePeriodDialog';

const getWavePeriodDate = (wavePeriodValue) => {
  if (!wavePeriodValue) return null;

  if (wavePeriodValue instanceof Date) return wavePeriodValue;

  if (typeof wavePeriodValue === 'string') {
    if (/^\d{6}$/.test(wavePeriodValue)) {
      const year = Number(wavePeriodValue.slice(0, 4));
      const month = Number(wavePeriodValue.slice(4, 6)) - 1;

      return new Date(year, month, 1);
    }

    const parsedDate = new Date(wavePeriodValue);
    if (!Number.isNaN(parsedDate.getTime())) return parsedDate;
  }

  return null;
};

const normalizeWavePeriodValue = (wavePeriodValue) => {
  const dateValue = getWavePeriodDate(wavePeriodValue);

  return dateValue ? formatWavePeriodValue(dateValue) : '';
};

const getWavePeriodLabel = (wavePeriodValue, intl) => {
  const dateValue = getWavePeriodDate(wavePeriodValue);

  if (!dateValue) return intl.messages.WAVEPERIOD;

  return capitalize(
    dateValue.toLocaleString(culture, { month: 'long', year: 'numeric' })
  );
};

const useStyles = () => ({
  root: {
    maxWidth: 'unset!important',
  },
  form: {
    display: 'flex',
    justifyContent: 'center',
  },
  gridItem: {
    padding: '16px 16px 0px 0px',
  },
  selectLabel: {
    borderRight: '1px solid',
    borderColor: (theme) => theme.palette.divider,
  },
  btnDiv: {
    width: '160px',
    display: 'flex',
    alignItems: 'flex-end',
  },
});

const ReportForm = function ReportForm({
  isDataLimited = false,
  currentFilters = null,
  setCurrentFilters = () => {},
  isOperator = false,
  hideOperatorFilter = false,
  title = '',
  sectionsOptions = [],
  wavePeriodOption = false,
}) {
  const classes = useStyles();
  const API = useAPI();
  const intl = useIntl();
  const [period, setPeriod] = useState('today'); //value for select items value
  const [grouping, setGrouping] = useState('M'); //time grouping (year, month, week)
  const [operator, setOperator] = useState(0); //selected operator
  const [branches, setBranches] = useState([]); //selected branches
  const [rangeDialog, setRangeDialog] = useState(false); //bool to display dialog
  const [periodRange, setPeriodRange] = useState([null, null]); //real date value [start, end]
  const userBranches = useAtomValue(userBranchesAtom);
  const [operatorsList, setOperatorsList] = useState([]); //list item for operator select
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [wavePeriodDialog, setWavePeriodDialog] = useState(false);
  const [wavePeriod, setWavePeriod] = useState(null);
  const userData = useAtomValue(userDataAtom);

  const groupingList = [
    {
      label: intl.messages.DAILY_GROUPING,
      value: 'D',
      active:
        period === 'month' ||
        (period === 'range' &&
          calculateDaysBetweenRange(
            new Date(periodRange[0]),
            new Date(periodRange[1])
          ) <= 60), //periodRange max 60 days for daily grouping
    },
    {
      label: intl.messages.WEEKLY_GROUPING,
      value: 'W',
      active: true,
    },
    {
      label: intl.messages.MONTHLY_GROUPING,
      value: 'M',
      active: true,
    },

    {
      label: intl.messages.YEARLY_GROUPING,
      value: 'Y',
      active: true,
    },
  ];

  useEffect(() => {
    if ((grouping !== 'D' && period === 'today') || period === 'yesterday')
      setGrouping('D');
    else if (grouping === 'D' && period === 'year') setGrouping('W');
  }, [period]);

  useEffect(() => {
    //if periodRange is bigger than 60 days and the selected grouping is "Daily" automatically changes the grouping
    if (
      grouping === 'D' &&
      period === 'range' &&
      calculateDaysBetweenRange(
        new Date(periodRange[0]),
        new Date(periodRange[1])
      ) > 60
    ) {
      setGrouping('W');
    }
  }, [periodRange]);

  useEffect(() => {
    if (currentFilters?.sections) setSections(currentFilters?.sections);
    else setSections(sectionsOptions.map(({ id }) => id));
    if (currentFilters?.branches) setBranches(currentFilters?.branches);
    else setBranches(userBranches.map(({ id }) => id));
    if (currentFilters?.wavePeriod)
      setWavePeriod(normalizeWavePeriodValue(currentFilters?.wavePeriod));
    if (currentFilters?.periodName) {
      setPeriod(currentFilters?.periodName);
      if (currentFilters?.periodName === 'range' && currentFilters?.startDate) {
        setPeriodRange([
          new Date(currentFilters?.startDate),
          new Date(currentFilters?.endDate),
        ]);
      }
      if (
        currentFilters?.periodName === 'wavePeriod' &&
        currentFilters?.wavePeriod
      ) {
        setWavePeriod(normalizeWavePeriodValue(currentFilters?.wavePeriod));
      }
    }
  }, [currentFilters]);

  const handleChangeBranches = (e) => {
    setBranches(e.target.value);
  };

  const handleChangeSection = (e) => {
    setSections(e.target.value);
  };

  useEffect(() => {
    if (!isOperator && !hideOperatorFilter) {
      API.getUsersByRoles(
        ['OPERATOR', 'SUPERVISOR'],
        (result = []) => {
          const array = result.filter(({ deletedOn }) => deletedOn === null);
          array.unshift({
            id: 0,
            name: intl.messages.NO_SELECTION,
            surname: '',
          });
          setOperatorsList(array);
        },
        (err) => {
          setError(err);
        }
      );
    } else {
      setOperatorsList([]);
    }
  }, [isOperator, hideOperatorFilter]);

  const handleRemoveBranch = (e, value) => {
    e.preventDefault();
    setBranches((prevBranches) =>
      prevBranches.filter((branch) => branch !== value)
    );
  };

  const handleRemoveSection = (e, value) => {
    e.preventDefault();
    setSections((prevSections) =>
      prevSections.filter((section) => section !== value)
    );
  };

  return (
    <Container sx={classes.root} disableGutters>
      <div style={classes.form}>
        <Grid container sx={{ width: '100%' }}>
          <Grid size={!isDataLimited ? 4 : 6} sx={classes.gridItem}>
            <ReportSelect
              defaultValue="range"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              classes={classes}
              fullWidth
              size="small"
              height="30px"
              padding="20px"
              label={intl.messages.PERIOD}
              onClickFunction={(selectedPeriod) => {
                if (selectedPeriod === 'range') {
                  setRangeDialog(true);
                } else if (selectedPeriod === 'wavePeriod') {
                  setWavePeriodDialog(true);
                }
              }}
              items={[
                {
                  label: intl.messages.TODAY,
                  value: 'today',
                  active: true,
                },
                {
                  label: intl.messages.YESTERDAY,
                  value: 'yesterday',
                  active: true,
                },
                {
                  label: intl.messages.CURRENT_WEEK,
                  value: 'week',
                  active: true,
                },
                {
                  label: intl.messages.LAST_WEEK,
                  value: 'lastWeek',
                  active: true,
                },
                {
                  label: intl.messages.CURRENT_MONTH,
                  value: 'month',
                  active: true,
                },
                ...(wavePeriodOption
                  ? [
                      {
                        label:
                          period === 'wavePeriod'
                            ? `${intl.messages.WAVEPERIOD}: ${getWavePeriodLabel(wavePeriod, intl)}`
                            : intl.messages.WAVEPERIOD,
                        value: 'wavePeriod',
                        active: true,
                      },
                    ]
                  : []),
                {
                  label:
                    period === 'range'
                      ? `${createNewDate(periodRange[0]).toLocaleDateString(
                          culture,
                          options
                        )}-${createNewDate(periodRange[1]).toLocaleDateString(
                          culture,
                          options
                        )}`
                      : intl.messages.PERIOD_RANGE,
                  value: 'range',
                  active: true,
                },
              ]}
            />
          </Grid>
          {!isDataLimited && (
            <Grid size={{ md: 4, sm: 4 }} sx={classes.gridItem}>
              <ReportSelect
                disabled={period === 'today' || period === 'yesterday'}
                value={grouping}
                onChange={(e) => setGrouping(e.target.value)}
                classes={classes}
                fullWidth
                size="small"
                height="30px"
                padding="20px"
                label={intl.messages.GROUP}
                items={groupingList}
              />
            </Grid>
          )}
          {!hideOperatorFilter && (
            <Grid size={!isDataLimited ? 4 : 6} sx={classes.gridItem}>
              <ReportSelect
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                classes={classes}
                fullWidth
                size="large"
                height="30px"
                padding="20px"
                label={intl.messages.OPERATOR}
                items={
                  error
                    ? [
                        {
                          label: intl.messages.DATA_LOADING_ERROR,
                          value: 'ERROR',
                        },
                      ]
                    : isOperator
                      ? [
                          {
                            id: 0,
                            name: intl.messages.NO_SELECTION,
                            surname: '',
                            active: true,
                          },
                          {
                            label: `${userData?.name} ${userData?.surname}`,
                            value: userData?.id,
                            active: true,
                          },
                        ]
                      : operatorsList?.map(({ id, name, surname }) => {
                          return {
                            label: `${name} ${surname}`,
                            value: id,
                            active: true,
                          };
                        })
                }
              />
            </Grid>
          )}
          <Grid size="grow" sx={classes.gridItem}>
            <ReportSelect
              value={branches}
              onChange={handleChangeBranches}
              classes={classes}
              fullWidth
              multiple
              size="large"
              padding="30px"
              label={intl.messages.BRANCH}
              items={userBranches?.map(({ id, branchDescription }) => {
                return {
                  label: capitalize(branchDescription),
                  value: id,
                  active: true,
                };
              })}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    maxHeight: '72px',
                    overflowY: 'auto',
                  }}
                >
                  {selected.map((value) => (
                    <Chip
                      clickable
                      key={`value-${value}`}
                      label={capitalize(
                        userBranches.find(({ id }) => id === value)
                          ?.branchDescription || value
                      )}
                      deleteIcon={
                        <CancelIcon
                          onMouseDown={(event) => event.stopPropagation()}
                        />
                      }
                      onDelete={(e) => handleRemoveBranch(e, value)}
                    />
                  ))}
                </Box>
              )}
            />
          </Grid>
          {sectionsOptions.length > 0 && (
            <Grid size={{ md: 6, sm: 6 }} sx={classes.gridItem}>
              <ReportSelect
                value={sections}
                onChange={handleChangeSection}
                classes={classes}
                fullWidth
                multiple
                size="large"
                padding="30px"
                label={intl.messages.SECTIONS}
                items={sectionsOptions?.map(({ id, name }) => {
                  return {
                    label: capitalize(name),
                    value: id,
                    active: true,
                  };
                })}
                renderValue={(selected) => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      maxHeight: '72px',
                      overflowY: 'auto',
                    }}
                  >
                    {selected.map((value) => (
                      <Chip
                        clickable
                        key={`value-${value}`}
                        label={capitalize(
                          sectionsOptions?.find(({ id }) => id === value)
                            ?.name || value
                        )}
                        deleteIcon={
                          <CancelIcon
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        }
                        onDelete={(e) => handleRemoveSection(e, value)}
                      />
                    ))}
                  </Box>
                )}
              />
            </Grid>
          )}
        </Grid>
        <div style={classes.btnDiv}>
          <Button
            fullWidth
            sx={{
              margin: '0 auto',
              display: 'block',
              textTransform: 'none',
            }}
            onClick={() => {
              setCurrentFilters({
                periodName: period,
                wavePeriod: period === 'wavePeriod' ? wavePeriod : null,
                startDate: period === 'range' ? periodRange[0] : null,
                endDate: period === 'range' ? periodRange[1] : null,
                grouping: {
                  id: grouping,
                  label: groupingList?.find(({ value }) => value === grouping)
                    ?.label,
                },
                branches:
                  branches.length > 0
                    ? branches
                    : userBranches.map(({ id }) => id),
                sections:
                  sections.length > 0
                    ? sections
                    : sectionsOptions.map(({ id }) => id),
                operator:
                  isOperator && operator
                    ? {
                        id: operator,
                        label: `${userData?.name} ${userData?.surname}`,
                      }
                    : operator === 0 ||
                        typeof operatorsList?.find(({ id }) => id === operator)
                          ?.name === 'undefined'
                      ? null
                      : {
                          id: operator,
                          label: `${
                            operatorsList?.find(({ id }) => id === operator)
                              ?.name
                          } ${
                            operatorsList?.find(({ id }) => id === operator)
                              ?.surname
                          }`,
                        },
              });
            }}
          >
            {intl.messages.SEARCH}
          </Button>
        </div>
      </div>
      <DateRangePicker
        open={rangeDialog}
        close={() => setRangeDialog(false)}
        setPeriodRange={setPeriodRange}
        isDataLimited={isDataLimited}
        title="PERIOD"
        periodRange={periodRange}
      />
      <WavePeriodDialog
        open={wavePeriodDialog}
        close={() => setWavePeriodDialog(false)}
        date={getWavePeriodDate(wavePeriod)}
        onSubmit={(selectedWavePeriod) => {
          setWavePeriod(normalizeWavePeriodValue(selectedWavePeriod));
          setWavePeriodDialog(false);
        }}
      />
    </Container>
  );
};

ReportForm.propTypes = {
  currentFilters: PropTypes.object,
  setCurrentFilters: PropTypes.func,
  isOperator: PropTypes.bool,
  isDataLimited: PropTypes.bool,
};

export default ReportForm;
